import { CALL_ADDITIONAL_DOCUMENT_TYPES } from "@features/vessel-call/create/constants";
import { t, type TFunction } from "i18next";
import { z } from "zod";
import { FORM_FIELDS } from "../constants";

const LANG_BASE = "vessel-call.request-departure.form-fields";
const MANDATORY_KEY = "form-validation.mandatory";
const MAX_LENGTH_KEY = "form-validation.max-length";
export const DEPARTURE_REMARKS_MAX_CHARS = 1000;
const OTHER_SPECIFY_LABEL_KEY = "vessel-call.additional-documents.other-specify";

const requiredString = (label: string) => z.string().min(1, t(MANDATORY_KEY, { label }));
const requiredStringMax = (max: number, label: string) =>
  z.string().min(1, t(MANDATORY_KEY, { label })).max(max, t(MAX_LENGTH_KEY, { label, max }));

const ALPHANUM_REGEX = /^[A-Za-z0-9\s]*$/;

const hasValue = (value: unknown) => {
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  if (typeof value === "string") return value.trim().length > 0;
  return false;
};

const parseDocumentDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const optionalDocumentDate = (label: string) =>
  z.union([z.date().nullable().optional(), z.string(), z.null()]).superRefine((value, ctx) => {
    if (value == null) return;
    if (typeof value === "string" && value.trim().length === 0) return;

    if (!parseDocumentDate(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t("form-validation.invalid-date", { label }),
      });
    }
  });

const getAdditionalDocumentSchema = () =>
  z
    .object({
      name: z.string(),
      url: z.string(),
      type: z.string(),
      [FORM_FIELDS.DOCUMENT_TYPE]: z.string().optional(),
      [FORM_FIELDS.OTHER_SPECIFY_TYPE]: z
        .string()
        .max(
          50,
          t(MAX_LENGTH_KEY, {
            label: t(OTHER_SPECIFY_LABEL_KEY),
            max: 50,
          }),
        )
        .optional(),
      [FORM_FIELDS.DOCUMENT_NAME]: z
        .string()
        .max(
          50,
          t(MAX_LENGTH_KEY, {
            label: t("vessel-call.additional-documents.document-name"),
            max: 50,
          }),
        )
        .optional(),
      [FORM_FIELDS.ISSUE_DATE]: optionalDocumentDate(
        t("vessel-call.additional-documents.issue-date"),
      ),
      [FORM_FIELDS.EXPIRY_DATE]: optionalDocumentDate(
        t("vessel-call.additional-documents.expiry-date"),
      ),
      [FORM_FIELDS.DOCUMENT_SUMMARY]: z
        .string()
        .max(
          300,
          t(MAX_LENGTH_KEY, {
            label: t("vessel-call.additional-documents.document-summary"),
            max: 300,
          }),
        )
        .optional(),
    })
    .superRefine((value, ctx) => {
      const hasAttachment = hasValue(value.url) || hasValue(value.name);
      if (!hasAttachment) return;

      if (!hasValue(value[FORM_FIELDS.DOCUMENT_TYPE])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t("vessel-call.additional-documents.document-type"),
          }),
          path: [FORM_FIELDS.DOCUMENT_TYPE],
        });
      }

      if (!hasValue(value[FORM_FIELDS.DOCUMENT_NAME])) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t("vessel-call.additional-documents.document-name"),
          }),
          path: [FORM_FIELDS.DOCUMENT_NAME],
        });
      }

      if (
        value[FORM_FIELDS.DOCUMENT_TYPE] === CALL_ADDITIONAL_DOCUMENT_TYPES.OTHER_SPECIFY &&
        !hasValue(value[FORM_FIELDS.OTHER_SPECIFY_TYPE])
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(OTHER_SPECIFY_LABEL_KEY),
          }),
          path: [FORM_FIELDS.OTHER_SPECIFY_TYPE],
        });
      }

      if (
        value[FORM_FIELDS.DOCUMENT_TYPE] === CALL_ADDITIONAL_DOCUMENT_TYPES.CERTIFICATE &&
        !hasValue(value[FORM_FIELDS.EXPIRY_DATE])
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t("vessel-call.additional-documents.expiry-date"),
          }),
          path: [FORM_FIELDS.EXPIRY_DATE],
        });
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const issueDate = parseDocumentDate(value[FORM_FIELDS.ISSUE_DATE]);
      const expiryDate = parseDocumentDate(value[FORM_FIELDS.EXPIRY_DATE]);

      if (issueDate && issueDate > today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-future", {
            label: t("vessel-call.additional-documents.issue-date"),
          }),
          path: [FORM_FIELDS.ISSUE_DATE],
        });
      }

      if (expiryDate && expiryDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-not-be-in-past", {
            label: t("vessel-call.additional-documents.expiry-date"),
          }),
          path: [FORM_FIELDS.EXPIRY_DATE],
        });
      }

      if (issueDate && expiryDate && issueDate > expiryDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.must-be-after", {
            subject: t("vessel-call.additional-documents.expiry-date"),
            object: t("vessel-call.additional-documents.issue-date"),
          }),
          path: [FORM_FIELDS.EXPIRY_DATE],
        });
      }
    });

const documentSchema = z.object({
  name: z.string(),
  url: z.string(),
  type: z.string(),
});

const optionalNumber = z.preprocess(
  (value) => {
    if (value === "" || value === null || value === undefined) {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? value : parsed;
  },
  z
    .number({
      invalid_type_error: t("form-validation.invalid-number", {
        label: t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_FORE}`),
      }),
    })
    .optional(),
);

const requiredNumber = (label: string) =>
  z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    },
    z.number({
      required_error: t(MANDATORY_KEY, { label }),
      invalid_type_error: t("form-validation.invalid-number", { label }),
    }),
  );

export const getRequestDepartureFormSchema = (
  t: TFunction,
  isDraftRequired: boolean,
  isCustomsBypass = false,
) =>
  z
    .object({
      [FORM_FIELDS.FIRST_VISIT]: z.boolean(),
      [FORM_FIELDS.MASTER_NAME]: requiredStringMax(
        100,
        t(`${LANG_BASE}.${FORM_FIELDS.MASTER_NAME}`),
      ).regex(
        ALPHANUM_REGEX,
        t("form-validation.regex-no-special-chars", {
          label: t(`${LANG_BASE}.${FORM_FIELDS.MASTER_NAME}`),
        }),
      ),
      [FORM_FIELDS.NEXT_PORT]: requiredString(t(`${LANG_BASE}.${FORM_FIELDS.NEXT_PORT}`)),
      [FORM_FIELDS.FINAL_DESTINATION]: z.string().optional(),
      [FORM_FIELDS.VESSEL_CONDITION]: requiredString(
        t(`${LANG_BASE}.${FORM_FIELDS.VESSEL_CONDITION}`),
      ),
      [FORM_FIELDS.DRAFT_FORE]: isDraftRequired
        ? requiredNumber(t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_FORE}`))
        : optionalNumber,
      [FORM_FIELDS.DRAFT_AFT]: isDraftRequired
        ? requiredNumber(t(`${LANG_BASE}.${FORM_FIELDS.DRAFT_AFT}`))
        : optionalNumber,
      [FORM_FIELDS.REQUEST_ANCHORAGE_CLEARANCE]: z.boolean().optional(),
      [FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION]: z.array(documentSchema).min(1, {
        message: t(MANDATORY_KEY, {
          label: t(`${LANG_BASE}.${FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION}`),
        }),
      }),
      [FORM_FIELDS.CARGO_MANIFEST]: z.array(documentSchema).optional(),
      [FORM_FIELDS.CUSTOMS_CLEARANCE]: z.array(documentSchema).optional(),
      [FORM_FIELDS.CUSTOMS_CLEARANCE_UPLOAD_HAPPENED]: z.boolean().optional(),
      [FORM_FIELDS.REMARKS]: z
        .string()
        .max(
          DEPARTURE_REMARKS_MAX_CHARS,
          t(MAX_LENGTH_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.REMARKS}`),
            max: DEPARTURE_REMARKS_MAX_CHARS,
          }),
        )
        .optional(),
      [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: z.array(getAdditionalDocumentSchema()).optional(),
    })
    .superRefine((data, ctx) => {
      if (
        !data[FORM_FIELDS.FIRST_VISIT] &&
        (!data[FORM_FIELDS.CARGO_MANIFEST] || data[FORM_FIELDS.CARGO_MANIFEST]?.length === 0)
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.CARGO_MANIFEST],
          code: z.ZodIssueCode.custom,
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.CARGO_MANIFEST}`),
          }),
        });
      }
      if (
        data[FORM_FIELDS.NEXT_PORT]?.slice(0, 2) === "AE" &&
        !data[FORM_FIELDS.FINAL_DESTINATION]?.trim()
      ) {
        ctx.addIssue({
          path: [FORM_FIELDS.FINAL_DESTINATION],
          code: z.ZodIssueCode.custom,
          message: t(MANDATORY_KEY, {
            label: t(`${LANG_BASE}.${FORM_FIELDS.FINAL_DESTINATION}`),
          }),
        });
      }

      if (isCustomsBypass && !data[FORM_FIELDS.CUSTOMS_CLEARANCE_UPLOAD_HAPPENED]) {
        ctx.addIssue({
          path: [FORM_FIELDS.ADDITIONAL_DOCUMENTS],
          code: z.ZodIssueCode.custom,
          message: t("vessel-call.request-departure.form-errors.customs-clearance-upload-required"),
        });
      }
    });

export type RequestDepartureFormData = z.infer<ReturnType<typeof getRequestDepartureFormSchema>>;
